export type JourneyInputAction =
  | Readonly<{ type: "OPEN"; href: string }>
  | Readonly<{ type: "ADVANCE"; direction: -1 | 1 }>
  | Readonly<{ type: "RETURN" }>
  | Readonly<{ type: "SAVE" }>
  | Readonly<{ type: "REMIX" }>
  | Readonly<{ type: "REVEAL_GUIDANCE" }>;

export interface PointerSample {
  readonly pointerId: number;
  readonly clientX: number;
  readonly clientY: number;
}

export type PointerUpResult = "CANCELLED" | "OPENED" | "REJECTED";

export interface KeySample {
  readonly key: string;
  readonly href?: string;
  readonly repeat?: boolean;
}

export type RadioEdge = "FIRST" | "LAST";
export type RadioIntent =
  | Readonly<{ type: "BOUNDARY"; edge: RadioEdge }>
  | Readonly<{ type: "MOVE"; direction: -1 | 1 }>
  | Readonly<{ type: "ACTIVATE" }>;

export interface InputRouter {
  pointerDown(sample: PointerSample): void;
  pointerMove(sample: PointerSample): void;
  pointerUp(sample: PointerSample, href?: string): PointerUpResult;
  pointerCancel(pointerId: number): void;
  wheel(deltaY: number): boolean;
  key(sample: KeySample): boolean;
  historyReturn(): void;
  radioEdge(key: string): RadioEdge | null;
  radioIntent(key: string): RadioIntent | null;
}

const OPEN_DISTANCE = 8;
const OPEN_VERTICAL_TRAVEL = 12;
const WHEEL_ADVANCE_DISTANCE = 48;

interface PointerTrack {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  cancelled: boolean;
}

export function createInputRouter(emit: (action: JourneyInputAction) => boolean | void): InputRouter {
  let pointer: PointerTrack | null = null;
  let wheelPixels = 0;

  function pointerMove(sample: PointerSample): void {
    if (pointer === null || pointer.pointerId !== sample.pointerId) return;
    const dx = sample.clientX - pointer.startX;
    const dy = sample.clientY - pointer.startY;
    if (Math.hypot(dx, dy) > OPEN_DISTANCE || Math.abs(dy) > OPEN_VERTICAL_TRAVEL) {
      pointer.cancelled = true;
    }
  }

  return {
    pointerDown(sample) {
      pointer = {
        pointerId: sample.pointerId,
        startX: sample.clientX,
        startY: sample.clientY,
        cancelled: false,
      };
    },
    pointerMove,
    pointerUp(sample, href) {
      if (pointer === null || pointer.pointerId !== sample.pointerId) return "CANCELLED";
      pointerMove(sample);
      const shouldOpen = !pointer.cancelled && href !== undefined;
      pointer = null;
      if (!shouldOpen) return "CANCELLED";
      return emit({ type: "OPEN", href }) === false ? "REJECTED" : "OPENED";
    },
    pointerCancel(pointerId) {
      if (pointer?.pointerId === pointerId) pointer = null;
    },
    wheel(deltaY) {
      if (!Number.isFinite(deltaY) || deltaY === 0) return false;
      if (pointer !== null) pointer.cancelled = true;
      wheelPixels += deltaY;
      if (Math.abs(wheelPixels) < WHEEL_ADVANCE_DISTANCE) return false;
      emit({ type: "ADVANCE", direction: wheelPixels < 0 ? -1 : 1 });
      wheelPixels = 0;
      return true;
    },
    key(sample) {
      if (sample.repeat) return false;
      let action: JourneyInputAction | null = null;
      switch (sample.key) {
        case "Enter":
          if (sample.href !== undefined) action = { type: "OPEN", href: sample.href };
          break;
        case "ArrowDown":
        case "j":
        case "J":
          action = { type: "ADVANCE", direction: 1 };
          break;
        case "Escape":
          action = { type: "RETURN" };
          break;
        case "s":
        case "S":
          action = { type: "SAVE" };
          break;
        case "r":
        case "R":
          action = { type: "REMIX" };
          break;
        case "?":
          action = { type: "REVEAL_GUIDANCE" };
          break;
      }
      if (action === null) return false;
      emit(action);
      return true;
    },
    historyReturn() {
      emit({ type: "RETURN" });
    },
    radioEdge(key) {
      if (key === "Home") return "FIRST";
      if (key === "End") return "LAST";
      return null;
    },
    radioIntent(key) {
      if (key === "Home") return { type: "BOUNDARY", edge: "FIRST" };
      if (key === "End") return { type: "BOUNDARY", edge: "LAST" };
      if (key === "ArrowUp" || key === "ArrowLeft") return { type: "MOVE", direction: -1 };
      if (key === "ArrowDown" || key === "ArrowRight") return { type: "MOVE", direction: 1 };
      if (key === "Enter" || key === " ") return { type: "ACTIVATE" };
      return null;
    },
  };
}
